import { describeElementForCharts } from '../../utils/analytics';
import {
	MAX_FORMULA_LENGTH, analyze, describeElement, formulaCurrency, formulaUnits, parse, references, serialize, typeLabel,
} from '../../utils/formula';
import { createFormulaRefResolver } from '../../utils/formulaTokens';

/**
 * A CHART'S CALCULATIONS, as the builder reads them (charts A8, plan §4.3,
 * §6.2): a measure worked out per item from a formula ("Calculate per
 * item…", `measures[i].perItem`), and a formula across the chart's other
 * measures ("Add formula measure", `measures[i].formula`). Pure functions
 * over the VENDORED formula module (utils/formula, byte-identical with
 * keepr-api's) and the vendored capability table, mirroring what keepr-api's
 * spec validator and engine refuse (keepr-api utils/analytics/spec.js
 * validatePerItem / validateFormulaMeasure, formulas.js compile) — in the
 * server's own words — so the builder says "no, and why" beside the field
 * before any request, and never sends a formula the server would refuse.
 *
 *   checkPerItem(text, ctx)          a per-item formula against the card's elements
 *   checkAcross(text, above, draft)  a formula across measures against the measures above it
 *
 * Each answers `{ state, … }`:
 *   'empty'    nothing typed yet
 *   'refused'  `{ code, message, position? }` — what the server would refuse;
 *              a `not_offered_yet` also carries what the formula answers
 *              (`type`, `description`, `reads`), so a saved one the person did
 *              not touch can still be read (builderModel: it is held, not blocked)
 *   'blocked'  `{ message }` — it reads a measure that is not finished yet
 *   'ok'       `{ type, description?, canonical, timeDependent, reads }`
 *
 * The server stays the authority: whatever it refuses anyway arrives with a
 * `path` and is shown on the same field (builderModel refusalKey).
 */

// ------------------------------------------------------------- the shapes

/** A formula across measures the builder edits. */
export const isFormulaMeasure = (m) => Boolean(m) && !m.keep && typeof m.formula === 'string';
/** A measure worked out per item from a formula, which the builder edits. */
export const isPerItemMeasure = (m) => Boolean(m) && !m.keep && typeof m.perItem === 'string';
/** A formula across measures in either form: edited, or kept as it came. */
export const takesFormulaSlot = (m) => isFormulaMeasure(m) || Boolean(m && m.keep && m.keep.formula !== undefined);

/** The words a person reads for a formula's result. */
export const RESULT_WORDS = Object.freeze({
	number: 'a number',
	bool: 'a yes/no',
	date: 'a date',
	datetime: 'a date and time',
	time: 'a time of day',
	text: 'text',
	money: 'an amount of money',
	measure: 'a measurement',
});

/** The sentence keepr-api's run answers a formula over money or a measurement with (plan.js NOT_YET). */
export const UNITS_AND_MONEY = 'Formulas that read or answer amounts of money or measurements arrive in a later release.';

const PER_ITEM_NO_CARD = "A chart of every card can't calculate per item. Pick a card first.";
// keepr-api refuses a formula typed past 2,000 characters by its length
// (`too_long`, strictPayload's sentence names the path); the builder says it
// in words, with the server's code.
const TYPED_TOO_LONG = `The formula is longer than ${MAX_FORMULA_LENGTH.toLocaleString('en-US')} characters. Shorten it.`;
// The one way to name a reference: choose it from the {{ }} button.
const CHOOSE_ELEMENT = 'Choose an element with the {{ }} button.';
const CHOOSE_MEASURE = 'Choose the measures with the {{ }} button.';
const TOO_LONG = `The formula is too long once written out in full (limit ${MAX_FORMULA_LENGTH.toLocaleString('en-US')} characters, spaces between its parts included). Shorten it.`;

// How much of a formula reference a sentence echoes back (keepr-api spec.js ECHO_MAX).
const ECHO_MAX = 60;
const echo = (text) => (text.length > ECHO_MAX ? `${text.slice(0, ECHO_MAX)}…` : text);

const refused = (code, message, extra = {}) => ({ state: 'refused', code, message, ...extra });

function invalid(e) {
	if (e && e.isFormulaError) {
		const position = Number.isInteger(e.position) ? e.position : null;
		// A reference to nothing on the card: the sentence says how to name one.
		const hint = /does not exist on this card\.$/.test(e.message) ? ` ${CHOOSE_ELEMENT}` : '';
		return refused('formula_invalid', `The formula can't be used: ${e.message}${hint}`, position === null ? {} : { position });
	}
	return refused('formula_invalid', "The formula can't be used.");
}

/**
 * The element a per-item formula's RESULT charts as: its type through the
 * capability table, as an element of that type would (keepr-api spec.js
 * elementForFormulaType). Text charts as long text — it can only be counted.
 */
export function elementForFormulaType(type) {
	if (!type) return null;
	switch (type.type) {
		case 'number': return { dataType: 'number', options: {} };
		case 'money': return { dataType: 'currency', options: { currencies: type.currencies || [] } };
		case 'measure': return { dataType: 'measurement', options: { measure: type.measure || undefined } };
		case 'text': return { dataType: 'text-large', options: {} };
		case 'bool': return { dataType: 'boolean', options: {} };
		case 'date': return { dataType: 'date', options: {} };
		case 'datetime': return { dataType: 'date-time', options: {} };
		case 'time': return { dataType: 'time', options: {} };
		default: return null; // a lookup, a list or a bare blank has nothing to aggregate
	}
}

/** The formula-language element a measure's answer reads as in a formula across measures (keepr-api spec.js elementForAnswer). */
export function elementForAnswer(answer, description) {
	switch (answer && answer.result) {
		case 'items': case 'share': case 'number': return { dataType: 'number', options: {} };
		case 'money': return { dataType: 'currency', options: { currencies: (description && description.currencies) || [] } };
		case 'measure': return { dataType: 'measurement', options: { measure: description && description.measure } };
		case 'date': return { dataType: description && description.kind === 'datetime' ? 'date-time' : 'date', options: {} };
		case 'time': return { dataType: 'time', options: {} };
		default: return null;
	}
}

/**
 * Every element a per-item formula may read: the chart card's own merged
 * elements, by name — keepr-api reads them from the card's merged list in
 * memory (spec.js ctx.byName), elements named like a system field included.
 * `ctx.elements` is that list; a context built without it falls back to the
 * fields' elements.
 */
export function formulaElements(ctx) {
	if (ctx && Array.isArray(ctx.elements)) return ctx.elements.filter((el) => el && typeof el.name === 'string' && el.name);
	return ((ctx && ctx.fields) || []).filter((f) => !f.system && f.element).map((f) => f.element);
}

const TERMINALS = ['createdAt', 'updatedAt'];

// The names a formula's text references, `{{…}}` read as written, whether or
// not it parses yet — so a formula being typed still counts as reading what it
// names (a hidden measure is not shown again mid-edit).
const TOKEN_RE = /\{\{([^{}]*)\}\}/g;
export function namesIn(text) {
	const out = new Set();
	if (typeof text !== 'string') return out;
	// A formula that parses names what the module says it references — a
	// {{m1}} inside a text literal is text, not a reference (keepr-api's
	// hidden_unused reads references() too).
	try {
		references(parse(text)).forEach((ref) => out.add(ref.path[0]));
		return out;
	} catch (e) {
		// Being typed: read the {{…}} as written.
	}
	let match;
	TOKEN_RE.lastIndex = 0;
	while ((match = TOKEN_RE.exec(text)) !== null) out.add(match[1].trim());
	return out;
}

/** Can a chart's formula name this element? A name holding `.` reads as a hop, one holding a brace can't be written inside {{ }}. */
export const referable = (name) => typeof name === 'string' && name !== '' && !/[.{}]/.test(name);

// ---------------------------------------------------------- per item

/**
 * A per-item formula ("Calculate per item…"), checked as keepr-api checks it:
 * a chart of every card can't calculate per item; it parses; it follows no
 * lookup to another item (the analyzer's own sentence points at a calculated
 * element); its references are the card's elements; its result is a type a
 * chart can measure; written out it fits 2,000 characters; and it neither
 * reads nor answers money or a measurement (the run's `not_offered_yet`,
 * `needs: 'unitsAndMoney'`). `description` is what the capability table
 * offers for its result, as for an element of that type.
 */
export function checkPerItem(text, ctx) {
	// keepr-api's order: the shape (its length) before the card relation.
	if (typeof text === 'string' && text.length > MAX_FORMULA_LENGTH) return refused('too_long', TYPED_TOO_LONG, { max: MAX_FORMULA_LENGTH });
	if (!ctx || !ctx.card) return refused('system_fields_only', PER_ITEM_NO_CARD);
	if (typeof text !== 'string' || !text.trim()) return { state: 'empty' };
	let ast;
	try { ast = parse(text); } catch (e) { return invalid(e); }
	const refs = references(ast);
	const hop = refs.find((ref) => ref.path.length > 1);
	if (hop) {
		return refused('formula_lookup_hop', `Charts can't follow {{${echo(hop.path.join('.'))}}} to another item yet. Make it a calculated element on the card and chart that.`);
	}
	const resolveRef = createFormulaRefResolver(formulaElements(ctx), null);
	let analysis;
	try {
		analysis = analyze(ast, { resolveRef, units: formulaUnits, currency: formulaCurrency });
	} catch (e) { return invalid(e); }
	const resultElement = elementForFormulaType(analysis.type);
	if (!resultElement) return refused('formula_result_unusable', `The formula answers ${typeLabel(analysis.type)}, which a chart can't measure.`);
	const canonical = serialize(ast);
	if (canonical.length > MAX_FORMULA_LENGTH) return refused('formula_invalid', TOO_LONG, { max: MAX_FORMULA_LENGTH });
	// What the run refuses (keepr-api formulas.js compile): a money or a
	// measurement read (dollars added to euros, pounds to kilograms) or answered.
	const readsQuantity = refs.some((ref) => {
		const t = resolveRef(ref.path.slice());
		return Boolean(t) && (t.type === 'money' || t.type === 'measure');
	});
	const reads = [];
	refs.forEach((ref) => { if (reads.indexOf(ref.path[0]) === -1) reads.push(ref.path[0]); });
	const description = describeElementForCharts(resultElement);
	if (readsQuantity || analysis.type.type === 'money' || analysis.type.type === 'measure') {
		return refused('not_offered_yet', UNITS_AND_MONEY, { needs: 'unitsAndMoney', type: analysis.type, description, canonical, reads });
	}
	return {
		state: 'ok',
		type: analysis.type,
		description,
		canonical,
		timeDependent: Boolean(analysis.timeDependent),
		reads,
	};
}

/** The names a per-item formula reads that this card no longer has (a card switch clears it). */
export function missingNames(text, ctx) {
	let ast;
	try { ast = parse(text); } catch (e) { return []; }
	const names = new Set(formulaElements(ctx).map((el) => el.name));
	const out = [];
	references(ast).forEach((ref) => {
		const name = ref.path[0];
		if (ref.path.length === 1 && !names.has(name) && TERMINALS.indexOf(name) === -1 && out.indexOf(name) === -1) out.push(name);
	});
	return out;
}

// ---------------------------------------------------------- across measures

/**
 * A formula across measures ("Add formula measure"), checked as keepr-api
 * checks it: it parses; it reads only measures ABOVE it, by their keys; each
 * reads as what that measure answers; it answers a number (an amount of money
 * or a measurement types, but the run refuses it — `not_offered_yet`); it
 * reads no money or measurement measure; written out it fits.
 *
 *   above   Map(key -> { answer, description, label, ready }) — the measures
 *           before this one, in order, each with what it answers
 *   below   Map(key -> label) — the chart's measures after it, so a
 *           reference to one says where it is
 */
export function checkAcross(text, above, below = new Map()) {
	if (typeof text !== 'string' || !text.trim()) return { state: 'empty' };
	if (text.length > MAX_FORMULA_LENGTH) return refused('too_long', TYPED_TOO_LONG, { max: MAX_FORMULA_LENGTH });
	let ast;
	try { ast = parse(text); } catch (e) { return invalid(e); }
	const refs = references(ast);
	for (let i = 0; i < refs.length; i += 1) {
		const path = refs[i].path;
		if (path.length !== 1 || !above.has(path[0])) {
			// keepr-api's code (formula_forward_reference), in words that say
			// where the measure is, or how to name one — its sentence speaks of
			// keys, which the builder never shows.
			if (path.length === 1 && below.has(path[0])) {
				return refused('formula_forward_reference', `${below.get(path[0])} comes after this formula. A formula across measures reads only the measures above it.`);
			}
			return refused('formula_forward_reference', `{{${echo(path.join('.'))}}} is not a measure above this one. ${CHOOSE_MEASURE}`);
		}
	}
	const reads = [];
	refs.forEach((ref) => { if (reads.indexOf(ref.path[0]) === -1) reads.push(ref.path[0]); });
	// A measure it reads that is not finished yet says so on its own field;
	// this one waits for it.
	const unready = reads.map((key) => above.get(key)).find((prior) => !prior.ready);
	if (unready) return { state: 'blocked', message: `This formula reads ${unready.label}, which isn't finished yet.` };
	const resolveRef = (p) => {
		const prior = p.length === 1 ? above.get(p[0]) : null;
		const el = prior ? elementForAnswer(prior.answer, prior.description) : null;
		return el ? describeElement(el) : null;
	};
	let analysis;
	try {
		analysis = analyze(ast, { resolveRef, units: formulaUnits, currency: formulaCurrency });
	} catch (e) { return invalid(e); }
	const t = analysis.type;
	if (!t || (t.type !== 'number' && t.type !== 'money' && t.type !== 'measure')) {
		return refused('formula_result_not_a_quantity', `The formula answers ${typeLabel(t)}; a formula across measures must answer a number, an amount of money or a measurement.`);
	}
	const canonical = serialize(ast);
	if (canonical.length > MAX_FORMULA_LENGTH) return refused('formula_invalid', TOO_LONG, { max: MAX_FORMULA_LENGTH });
	const readsQuantity = reads.some((key) => {
		const result = above.get(key).answer && above.get(key).answer.result;
		return result === 'money' || result === 'measure';
	});
	if (readsQuantity || t.type !== 'number') return refused('not_offered_yet', UNITS_AND_MONEY, { needs: 'unitsAndMoney', type: t, canonical, reads });
	return { state: 'ok', type: t, canonical, timeDependent: Boolean(analysis.timeDependent), reads };
}
